
import { useEffect, useRef, type ReactNode } from 'react';

interface InteractiveElementsProps {
  children: ReactNode;
}

export default function InteractiveElements({ children }: InteractiveElementsProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Add interactive areas to empty spaces
    const addInteractiveAreas = () => {
      const sections = container.querySelectorAll('section');
      
      sections.forEach((section) => {
        // Create floating liquid effects
        for (let i = 0; i < 2; i++) {
          const liquidEffect = document.createElement('div');
          liquidEffect.className = 'liquid-effect';
          liquidEffect.style.position = 'absolute';
          liquidEffect.style.top = Math.random() * 80 + 10 + '%';
          liquidEffect.style.left = Math.random() * 80 + 10 + '%';
          liquidEffect.style.animationDelay = Math.random() * 12 + 's';
          liquidEffect.style.pointerEvents = 'none';
          liquidEffect.style.zIndex = '1';
          
          section.style.position = 'relative';
          section.appendChild(liquidEffect);
        }

        // Add hover effects to empty areas
        const emptyAreas = section.querySelectorAll('.container > div:empty, .max-w-6xl > div:empty');
        emptyAreas.forEach((area) => {
          (area as HTMLElement).classList.add('interactive-area');
        });
      });
    };

    // Parallax effect for scroll
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const elements = container.querySelectorAll('.morphing-blob, .liquid-effect');
      
      elements.forEach((element, index) => {
        const speed = 0.5 + (index * 0.1);
        const yPos = -(scrollY * speed);
        (element as HTMLElement).style.transform = `translate3d(0, ${yPos}px, 0)`;
      });
    };

    // Magnetic effect for interactive elements
    const addMagneticEffect = () => {
      const interactiveElements = container.querySelectorAll('.interactive-area, button, a');
      
      interactiveElements.forEach((element) => {
        element.addEventListener('mouseenter', (e) => {
          const target = e.target as HTMLElement;
          target.style.transition = 'transform 0.3s ease-out';
        });

        element.addEventListener('mousemove', (e) => {
          const target = e.target as HTMLElement;
          const rect = target.getBoundingClientRect();
          const x = e.clientX - rect.left - rect.width / 2;
          const y = e.clientY - rect.top - rect.height / 2;
          
          const moveX = x * 0.1;
          const moveY = y * 0.1;
          
          target.style.transform = `translate(${moveX}px, ${moveY}px)`;
        });

        element.addEventListener('mouseleave', (e) => {
          const target = e.target as HTMLElement;
          target.style.transform = 'translate(0, 0)';
        });
      });
    };

    // Initialize effects
    setTimeout(() => {
      addInteractiveAreas();
      addMagneticEffect();
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {children}
    </div>
  );
}
